/**
 * Reports: choose the two axes, read the matrix, export the file.
 *
 * The pivot is configured, not hardcoded, because no two questions about a project's money have
 * the same shape - "which phase overspent" and "what did we pay each contractor per quarter" are
 * the same data on different axes. Configurations worth keeping are saved for everyone.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Download, FloppyDisk, Trash, FilePdf, FileXls, FileCsv } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../../context/AuthContext';
import { cn } from '../../../lib/utils';
import { PivotMatrix } from './PivotMatrix';

const MEASURE_CHIPS = [
  { key: 'budget', label: 'Budget' },
  { key: 'actual', label: 'Actual' },
  { key: 'variance', label: 'Variance' },
  { key: 'variance_percent', label: 'Variance %' },
];

// Costs and revenue are never summed; this picks which side of the ledger you are reading
const BASIS_OPTIONS = [
  { key: 'split', label: 'Cost + revenue' },
  { key: 'net', label: 'Net' },
  { key: 'costs', label: 'Costs' },
  { key: 'income', label: 'Revenue' },
];

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const FinanceReportsPanel = ({ projectId, projectType, currency = 'EUR', onDrill }) => {
  const [dimensions, setDimensions] = useState([]);
  const [config, setConfig] = useState({
    rows: 'phase',
    columns: 'month',
    measures: ['budget', 'actual', 'variance'],
    basis: 'split',
    start_date: '',
    end_date: '',
  });
  const [pivot, setPivot] = useState(null);
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    api.get('/admin/v2/finance/reports/dimensions')
      .then((res) => setDimensions(res.data.dimensions || []))
      .catch(() => {});
    api.get(`/admin/v2/finance/projects/${projectId}/reports/views`)
      .then((res) => setViews(res.data.views || []))
      .catch(() => {});
  }, [projectId]);

  const body = useCallback(() => ({
    rows: config.rows,
    columns: config.columns,
    measures: config.measures,
    basis: config.basis,
    project_type: projectType || undefined,
    start_date: config.start_date || undefined,
    end_date: config.end_date || undefined,
  }), [config, projectType]);

  const loadPivot = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/v2/finance/projects/${projectId}/reports/pivot`, body());
      setPivot(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not build that pivot');
    } finally {
      setLoading(false);
    }
  }, [projectId, body]);

  useEffect(() => { loadPivot(); }, [loadPivot]);

  const exportPivot = async (format) => {
    setExporting(format);
    try {
      const res = await api.post(
        `/admin/v2/finance/projects/${projectId}/reports/pivot/export?format=${format}`,
        body(),
        { responseType: 'blob' }
      );
      downloadBlob(res.data, `pivot-${config.rows}-by-${config.columns}.${format}`);
      toast.success(`${format.toUpperCase()} exported`);
    } catch {
      toast.error(`Could not export the ${format.toUpperCase()}`);
    } finally {
      setExporting('');
    }
  };

  const exportPack = async (format) => {
    setExporting(`pack-${format}`);
    try {
      const res = await api.get(
        `/admin/v2/finance/projects/${projectId}/reports/pack/export`,
        { params: { format, project_type: projectType || undefined }, responseType: 'blob' }
      );
      downloadBlob(res.data, `statement-pack.${format}`);
      toast.success('Statement pack exported');
    } catch {
      toast.error('Could not export the statement pack');
    } finally {
      setExporting('');
    }
  };

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) return;
    try {
      await api.post(`/admin/v2/finance/projects/${projectId}/reports/views`, { name, config });
      const res = await api.get(`/admin/v2/finance/projects/${projectId}/reports/views`);
      setViews(res.data.views || []);
      setViewName('');
      toast.success(`View "${name}" saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the view');
    }
  };

  const applyView = (view) => {
    setConfig((prev) => ({ ...prev, ...view.config, measures: view.config.measures || prev.measures }));
    toast.success(`Showing "${view.name}"`);
  };

  const deleteView = async (view) => {
    try {
      await api.delete(`/admin/v2/finance/reports/views/${view.id}`);
      setViews((prev) => prev.filter((v) => v.id !== view.id));
      toast.success(`"${view.name}" removed`);
    } catch {
      toast.error('Could not remove the view');
    }
  };

  const toggleMeasure = (key) => {
    setConfig((prev) => {
      const next = prev.measures.includes(key)
        ? prev.measures.filter((m) => m !== key)
        : [...prev.measures, key];
      return { ...prev, measures: next.length ? next : prev.measures };
    });
  };

  // A cell is an intersection of two dimensions, so drilling carries both as journal filters
  const drill = (row, column) => {
    if (!onDrill) return;
    const FILTERABLE = { phase: 'phase', category: 'category', month: 'month',
      counterparty: 'counterparty', entry_type: 'entry_type' };
    const filters = {};
    if (FILTERABLE[config.rows] && row?.key) filters[FILTERABLE[config.rows]] = row.key;
    if (FILTERABLE[config.columns] && column?.key) filters[FILTERABLE[config.columns]] = column.key;
    if (config.start_date) filters.start_date = config.start_date;
    if (config.end_date) filters.end_date = config.end_date;
    onDrill(filters);
  };

  return (
    <div className="space-y-4" data-testid="finance-reports-panel">
      <section className="bg-white rounded-xl border border-black/[0.04]">
        <header className="flex flex-wrap items-end justify-between gap-3 px-4 py-3 border-b border-black/[0.05]">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Label className="text-xs">Rows</Label>
              <Select value={config.rows} onValueChange={(value) => setConfig((p) => ({ ...p, rows: value }))}>
                <SelectTrigger className="h-9" data-testid="pivot-rows-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dimensions.filter((d) => d.value !== 'none').map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Columns</Label>
              <Select value={config.columns} onValueChange={(value) => setConfig((p) => ({ ...p, columns: value }))}>
                <SelectTrigger className="h-9" data-testid="pivot-columns-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dimensions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                className="h-9"
                value={config.start_date}
                onChange={(e) => setConfig((p) => ({ ...p, start_date: e.target.value }))}
                data-testid="pivot-start-date"
              />
            </div>
            <div className="w-36">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                className="h-9"
                value={config.end_date}
                onChange={(e) => setConfig((p) => ({ ...p, end_date: e.target.value }))}
                data-testid="pivot-end-date"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center gap-1 p-1 rounded-lg bg-black/[0.04]" data-testid="pivot-basis-group">
              {BASIS_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  onClick={() => setConfig((p) => ({ ...p, basis: option.key }))}
                  className={cn(
                    'px-2.5 py-1 text-xs rounded-md transition-colors whitespace-nowrap',
                    config.basis === option.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted'
                  )}
                  data-testid={`pivot-basis-${option.key}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {MEASURE_CHIPS.map((chip) => (
              <button
                key={chip.key}
                onClick={() => toggleMeasure(chip.key)}
                className={cn(
                  'px-2.5 py-1 text-xs rounded-full border transition-colors',
                  config.measures.includes(chip.key)
                    ? 'bg-foreground text-white border-transparent'
                    : 'border-black/[0.12] text-foreground-muted hover:border-black/25'
                )}
                data-testid={`pivot-measure-${chip.key}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <p className="py-12 text-center text-sm text-foreground-muted">Building the matrix…</p>
        ) : (
          <PivotMatrix pivot={pivot} currency={currency} onDrill={onDrill ? drill : undefined} />
        )}

        <footer className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-black/[0.05]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-muted mr-1">
              {config.basis === 'split'
                ? 'Exports carry a separate table per side of the ledger'
                : `Exporting ${BASIS_OPTIONS.find((o) => o.key === config.basis)?.label.toLowerCase()} only`}
            </span>
            <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportPivot('csv')} data-testid="pivot-export-csv">
              <FileCsv className="h-4 w-4 mr-1.5" />
              CSV
            </Button>
            <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportPivot('xlsx')} data-testid="pivot-export-xlsx">
              <FileXls className="h-4 w-4 mr-1.5" />
              Excel
            </Button>
            <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportPivot('pdf')} data-testid="pivot-export-pdf">
              <FilePdf className="h-4 w-4 mr-1.5" />
              PDF
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Save this view as…"
              className="h-9 w-48"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              data-testid="pivot-view-name"
            />
            <Button variant="outline" size="sm" onClick={saveView} disabled={!viewName.trim()} data-testid="pivot-save-view">
              <FloppyDisk className="h-4 w-4 mr-1.5" />
              Save view
            </Button>
          </div>
        </footer>
      </section>

      {views.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-1" data-testid="pivot-saved-views">
          <span className="text-xs text-foreground-muted">Saved views</span>
          {views.map((view) => (
            <span
              key={view.id}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full border border-black/[0.12] text-xs"
            >
              <button onClick={() => applyView(view)} data-testid={`pivot-view-${view.name}`}>
                {view.name}
                {!view.project_id && <span className="ml-1 text-foreground-muted">· all projects</span>}
              </button>
              <button
                className="text-foreground-muted hover:text-red-600"
                onClick={() => deleteView(view)}
                data-testid={`pivot-view-delete-${view.name}`}
                title={`Remove ${view.name}`}
              >
                <Trash className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <section className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl border border-black/[0.06] bg-black/[0.015]">
        <p className="text-sm text-foreground-muted max-w-2xl">
          <strong className="font-medium text-foreground">Statement pack</strong> — key figures,
          budget vs actual by phase, category and account, profit and loss, monthly cash flow and
          the approved budget revisions, in one file with the project and generation stamp on every
          sheet.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!!exporting} onClick={() => exportPack('xlsx')} data-testid="pack-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" />
            Excel pack
          </Button>
          <Button size="sm" disabled={!!exporting} onClick={() => exportPack('pdf')} data-testid="pack-export-pdf">
            <FilePdf className="h-4 w-4 mr-1.5" />
            PDF pack
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FinanceReportsPanel;
